import queryString, { StringifyOptions } from 'query-string';
import { isDev } from './common';
import {
  ECOMMERCE_DEVELOPMENT_PREVIEW_ROOT,
  ECOMMERCE_DEVELOPMENT_ROOT
} from 'constants/links';
import { CATCORP_ESITE } from 'constants/common';
import { replaceParamsInStringTemplate } from './string';

export const getRelativeURLWithParams = (
  partialURL: string,
  query: Query,
  options: StringifyOptions = {}
) => {
  return queryString.stringifyUrl({ url: partialURL, query }, options);
};

export const getFormattedURL = (
  host: string,
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  const url = `https://${host}${partialURL}`;
  return getRelativeURLWithParams(url, query, options);
};

export const getFormattedIibURL = (
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  return getFormattedURL(
    process.env.IIB_API_HOST ?? '',
    partialURL,
    query,
    options
  );
};

export const getFormattedLsiURL = (
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  const formattedQuery = {
    ...query,
    ...(query.storeIdentifier === 'catcorp'
      ? {
          storeIdentifier: 'CATCorp'
        }
      : {})
  };
  return getFormattedURL(
    process.env.LSI_API_HOST ?? '',
    partialURL,
    formattedQuery,
    options
  );
};

export const getFormattedContentDeliveryURL = (
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  return getFormattedURL(
    process.env.OMM_API_HOST ?? '',
    partialURL,
    query,
    options
  );
};

export const getFormattedMntURL = (
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  return getFormattedURL(
    process.env.BFF_MNT_API_HOST ?? '',
    partialURL,
    query,
    options
  );
};

export const getFormattedMiscURL = (
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  return getFormattedURL(
    process.env.MISC_API_HOST ?? '',
    `${partialURL}`,
    query,
    options
  );
};

export const getFormattedApigeeURL = (
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  return getFormattedURL(
    process.env.APIGEE_API_HOST ?? '',
    `${partialURL}`,
    query,
    options
  );
};

export const getFormattedStageApigeeURL = (
  partialURL: string,
  query: Query = {},
  options: StringifyOptions = {}
) => {
  return getFormattedURL(
    process.env.APIGEE_STAGE_API_HOST ?? '',
    `${partialURL}`,
    query,
    options
  );
};

export const replaceParamsInURL = (URL: string, params) => {
  return replaceParamsInStringTemplate(URL, params);
};

/**
 * Transform params with shape `{param}` to `:param`
 * @example
 * "/store/{storeId}/catbundle/{langId}/keys" => "/store/:storeId/catbundle/:langId/keys"
 * "/eCommerce/locations/{locationId}" => "/eCommerce/locations/:locationId"
 */
export const transformParamsInURL = (url: string) =>
  url.replace(/{/g, ':').replace(/}/g, '');

export const normalizeUrl = (url = '') => {
  const getLeadingForwardSlash = (urlString: string) =>
    urlString[0] !== '/' ? '/' : '';
  if (
    isDev() &&
    typeof url === 'string' &&
    url.substring(0, 4) !== 'http' &&
    url.substring(0, 4) !== 'tel:' &&
    !url.startsWith(ECOMMERCE_DEVELOPMENT_ROOT) &&
    !url.startsWith(ECOMMERCE_DEVELOPMENT_PREVIEW_ROOT)
  ) {
    url = `${ECOMMERCE_DEVELOPMENT_ROOT}${getLeadingForwardSlash(url)}${url}`;
  }

  return url;
};

export const getCatOrDealerLegalURL = (
  pathname: string,
  lang: string,
  isCatCorp: boolean,
  seoURL?: string
) => {
  const currentUrl = isCatCorp ? `/${lang}/${CATCORP_ESITE}` : seoURL;
  return getRelativeURLWithParams(`${currentUrl}${pathname}`, {});
};
